import {
  formatDateColumnValue,
  formatNumberColumnValue,
  isResolvedFormulaCellData,
  isResolvedFormulaDateCellData,
  isResolvedFormulaDateTimeCellData,
  isResolvedFormulaStringCellData,
  OrganizationFeatureFlag,
} from '@doss/common-all'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PiFunctionBold } from 'react-icons/pi'
import { RxCopy } from 'react-icons/rx'
import { BaseInputWrapperProps } from '~/@/components/inputs/base/types'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { DataType } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getFormulaErrorMessageKey } from '~/common/utils/formula-error'
import { FormulaPopover } from '~/components/formula/FormulaPopover'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
import { RecordDetailTableCell } from '../types'

interface RecordFormulaCellProps {
  cell: RecordDetailTableCell
  tableId: string
  inputWrapperProps?: BaseInputWrapperProps
}

export function RecordFormulaCell({ cell, tableId, inputWrapperProps }: RecordFormulaCellProps) {
  const { locale, user } = useGlobalContext()
  const { t } = useTypedTranslation()
  // formula_error.* keys aren't in the typed i18n key set yet (blocked by an unrelated
  // pre-existing fr/es/zh translation gap that prevents regenerating it) -- use the untyped hook
  // for just this lookup until that's resolved.
  const { t: tUntyped } = useTranslation()

  const [showFormula, setShowFormula] = useState(false)
  const [isHoveringOverFormulaCell, setIsHoveringOverFormulaCell] = useState(false)
  const formulaAnchorEl = useRef<HTMLDivElement>(null)
  const [formulaTooltipText, setFormulaTooltipText] = useState('Copy to clipboard')
  const [formulaTooltipOpen, setFormulaTooltipOpen] = useState(false)
  const [formulaTooltipDelay, setFormulaTooltipDelay] = useState(0)

  // Rows are already loaded by the time this renders, so a non-resolved cell here means the
  // formula has no computed value (not yet computed, or computed to null) -- not that we're
  // still waiting on data.
  const value = useMemo(() => {
    switch (cell.column.type) {
      case DataType.FORMULA_STRING:
        return isResolvedFormulaStringCellData(cell.data) ? cell.data.data : null
      case DataType.FORMULA_DATE: {
        if (!isResolvedFormulaDateCellData(cell.data)) {
          return null
        }
        return formatDateColumnValue(
          cell.data.data,
          {
            formatPreset: cell.column.formatPreset,
            formatOptions: cell.column.formatOptions,
            isDateTime: false,
          },
          locale,
        )
      }
      case DataType.FORMULA_DATE_TIME: {
        if (!isResolvedFormulaDateTimeCellData(cell.data)) {
          return null
        }
        return formatDateColumnValue(
          cell.data.data,
          {
            formatPreset: cell.column.formatPreset,
            formatOptions: cell.column.formatOptions,
            isDateTime: true,
          },
          locale,
        )
      }
      default:
        if (!isResolvedFormulaCellData(cell.data)) {
          return null
        }
        return formatNumberColumnValue(cell.data.data, cell.column, locale)
    }
  }, [cell, locale])

  const isNumericFormula = cell.column.type === DataType.FORMULA_NUMBER
  const numericFormulaRuntimeErrorsEnabled =
    user?.organizationFeatureFlags[OrganizationFeatureFlag.NumericFormulaRuntimeErrors] === true
  const formulaErrorMessageKey = getFormulaErrorMessageKey(
    cell.formulaError,
    isNumericFormula,
    numericFormulaRuntimeErrorsEnabled,
  )
  const translateUntyped = tUntyped as (key: string) => string
  const formulaErrorMessage = formulaErrorMessageKey
    ? translateUntyped(formulaErrorMessageKey)
    : undefined

  const handleCopyClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Stop propagation so that the formula popover doesn't open
      event.stopPropagation()

      // Sets the delay to 500ms so that the tooltip doesn't disappear too quickly
      setFormulaTooltipDelay(500)

      // Copies the text to the clipboard
      if (value !== null) {
        navigator.clipboard.writeText(value)
      }

      // Sets the tooltip text to 'Copied!'
      setFormulaTooltipText(t('common.copied'))

      // Sets the tooltip to close after 500ms
      setTimeout(() => {
        setFormulaTooltipOpen(false)
        setFormulaTooltipDelay(0)
      }, 500)

      // Sets the tooltip text back to 'Copy to clipboard' after 1 second so that it doesn't flash
      // to the wrong text before the tooltip closes
      setTimeout(() => {
        setFormulaTooltipText(t('common.copy_to_clipboard'))
      }, 1000)
    },
    [t, value],
  )

  return (
    <div ref={formulaAnchorEl}>
      <ShortTextInput
        className="cursor-pointer"
        endAdornment={
          <Tooltip
            arrow
            enterDelay={100}
            leaveDelay={formulaTooltipDelay}
            onClose={() => setFormulaTooltipOpen(false)}
            onOpen={() => setFormulaTooltipOpen(true)}
            open={formulaTooltipOpen}
            placement="top"
            title={formulaTooltipText}
          >
            <button className="me-1" onClick={(event) => handleCopyClick(event)} type="button">
              {isHoveringOverFormulaCell ? (
                <RxCopy className="size-3.5" />
              ) : (
                <PiFunctionBold className="size-3.5" />
              )}
            </button>
          </Tooltip>
        }
        onClick={() => setShowFormula(true)}
        onMouseEnter={() => setIsHoveringOverFormulaCell(true)}
        onMouseLeave={() => setIsHoveringOverFormulaCell(false)}
        readOnly={true}
        value={value ?? ''}
        {...inputWrapperProps}
        errorText={formulaErrorMessage ?? inputWrapperProps?.errorText}
      />
      {showFormula && 'formula' in cell.column && (
        <FormulaPopover
          align="right"
          anchorEl={formulaAnchorEl.current}
          formulaId={cell.column.formula?.id}
          onClose={() => setShowFormula(false)}
          tableId={tableId}
        />
      )}
    </div>
  )
}
